import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Compass, Users } from "lucide-react";
import { EmptyState, LoadingState } from "@tabipla/ui";
import { Button } from "@tabipla/ui/components/button";
import { MekkeLayout } from "../layouts/MekkeLayout";
import { PublicBookletCard } from "../components/discover/PublicBookletCard";
import { usePublicBooklets, useTimelineFeed } from "../../application/hooks/useDiscover";
import { useAuth } from "../../application/hooks/useAuth";

type Tab = "discover" | "timeline";

export function DiscoverPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("discover");

  const { data: publicBooklets, isLoading: publicLoading } = usePublicBooklets();
  const { data: timelineBooklets, isLoading: timelineLoading } = useTimelineFeed({
    enabled: isAuthenticated && activeTab === "timeline",
  });

  const isLoading = activeTab === "discover" ? publicLoading : timelineLoading;
  const booklets = activeTab === "discover" ? publicBooklets : timelineBooklets;

  return (
    <MekkeLayout title={t("discover.title")}>
      <div className="animate-fade-in space-y-6">
        {/* Header Section */}
        <section>
          <h1 className="text-2xl font-bold">{t("discover.title")}</h1>
          <p className="mt-1 text-muted-foreground">
            {activeTab === "discover" ? t("discover.newBooklets") : t("discover.timelineDesc")}
          </p>
        </section>

        {/* Tab Buttons */}
        {isAuthenticated && (
          <div className="flex gap-2 border-b pb-2">
            <Button
              variant={activeTab === "discover" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("discover")}
              className="gap-2"
            >
              <Compass className="h-4 w-4" />
              {t("discover.tabDiscover")}
            </Button>
            <Button
              variant={activeTab === "timeline" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("timeline")}
              className="gap-2"
            >
              <Users className="h-4 w-4" />
              {t("discover.tabTimeline")}
            </Button>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <LoadingState message={t("common.loading")} />
        ) : (
          <section className="space-y-4">
            {booklets && booklets.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {booklets.map((booklet) => (
                  <PublicBookletCard key={booklet.id} booklet={booklet} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={activeTab === "discover" ? Compass : Users}
                title={activeTab === "discover" ? t("discover.empty") : t("discover.timelineEmpty")}
                description={
                  activeTab === "discover" ? t("discover.empty") : t("discover.timelineEmptyDesc")
                }
                variant="dashed"
              />
            )}
          </section>
        )}
      </div>
    </MekkeLayout>
  );
}
